import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FaLeaf, FaMinus, FaPlus, FaUtensils } from "react-icons/fa";
import { FiGrid, FiInfo, FiSearch, FiShoppingBag } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { categoryIconsMap } from "../../../config/categoryIcons";
import { formatPersianPrice, getCategoryKey, parseTableNumber } from "../../../services/customerMenuModel";
import MenuIntro from "../components/MenuIntro";
import MenuSplash from "../components/MenuSplash";
import LeafSprig from "./gardenFresh/LeafSprig";
import BamizbanBadge from "../components/BamizbanBadge";

const persianNumber = new Intl.NumberFormat("fa-IR");
const GREEN = "#3F5A3D";
const TERRACOTTA = "#B0552F";

const GardenFreshItemCard = ({ item, quantity = 0, onAdd, onDecrease, onClick }) => (
  <article className="relative flex gap-3 rounded-3xl bg-white p-3 shadow-sm">
    <button type="button" onClick={() => onClick(item)} className="min-w-0 flex-1 text-right focus:outline-none focus:underline">
      <h3 className="font-bold text-gray-900">{item.name}</h3>
      {item.description && <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">{item.description}</p>}
      <p className="mt-2 text-sm font-black" style={{ color: TERRACOTTA }}>{formatPersianPrice(item.price)}</p>
    </button>
    <div className="relative h-20 w-20 shrink-0">
      <button type="button" onClick={() => onClick(item)} aria-label={`مشاهده جزئیات ${item.name}`} className="h-full w-full overflow-hidden rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#3F5A3D]/30" style={item.image ? undefined : { background: "radial-gradient(circle at 30% 25%, #E4DEC9, #D8CFB0)" }}>
        {item.image ? <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" /> : <span className="flex h-full w-full items-center justify-center text-3xl" style={{ color: "#7C9067" }}><FaLeaf aria-hidden="true" /></span>}
      </button>
      {quantity > 0 ? (
        <div className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-white px-1 py-1 shadow" aria-label={`تعداد ${item.name}`}>
          <button type="button" onClick={() => onDecrease(item)} aria-label={`کم کردن ${item.name}`} className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#3F5A3D]/30"><FaMinus size={9} /></button>
          <span className="w-4 text-center text-xs font-bold" aria-live="polite">{persianNumber.format(quantity)}</span>
          <button type="button" onClick={() => onAdd(item)} aria-label={`اضافه کردن ${item.name}`} className="flex h-6 w-6 items-center justify-center rounded-full text-white focus:outline-none focus:ring-2 focus:ring-[#3F5A3D]/30" style={{ backgroundColor: GREEN }}><FaPlus size={9} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => onAdd(item)} aria-label={`افزودن ${item.name} به سبد`} className="absolute -bottom-2 left-1 flex h-8 w-8 items-center justify-center rounded-full text-white shadow focus:outline-none focus:ring-2 focus:ring-[#3F5A3D]/30" style={{ backgroundColor: GREEN }}><FaPlus size={12} /></button>
      )}
    </div>
  </article>
);

const GardenFreshMenuTheme = (props) => {
  const {
    menuData,
    restaurant,
    settings,
    profileImage,
    coverImage,
    categories,
    visibleItems,
    activeCategoryKey,
    setActiveCategoryKey,
    searchTerm,
    setSearchTerm,
    quantities,
    cartSummary,
    addItem,
    decreaseItem,
    openItem,
    openCart,
    showBamizbanBadge,
  } = props;
  const [infoOpen, setInfoOpen] = useState(false);
  const location = useLocation();
  const tableNumber = parseTableNumber(location.search);
  const reduceMotion = useReducedMotion();

  return (
    <div className="min-h-screen pb-28" dir="rtl" style={{ backgroundColor: "#F6F2E7" }}>
      <MenuSplash
        restaurantName={restaurant.name || menuData.title}
        profileImage={profileImage}
        backgroundColor={GREEN}
        accentColor="rgba(255,255,255,0.7)"
        tagline="انتخاب سالم برای فردایی بهتر"
        titleFont="font-serif"
        Decoration={LeafSprig}
      />

      <section className="relative h-56 overflow-hidden sm:h-64">
        <img src={coverImage} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-[#F6F2E7]" />
        <LeafSprig className="pointer-events-none absolute -right-2 -top-3 h-24 w-16 text-white/70 drop-shadow-[0_1px_3px_rgba(0,0,0,0.45)]" />
        <LeafSprig className="pointer-events-none absolute -bottom-6 -left-3 h-28 w-20 rotate-[150deg] text-white/50 drop-shadow-[0_1px_3px_rgba(0,0,0,0.45)]" />
        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
          <button type="button" onClick={() => setInfoOpen(true)} aria-label="اطلاعات رستوران" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow focus:outline-none focus:ring-2 focus:ring-white" style={{ color: GREEN }}><FiInfo /></button>
          {tableNumber && <span className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold shadow" style={{ color: GREEN }}>میز {persianNumber.format(tableNumber)}</span>}
        </div>
      </section>

      <main className="relative -mt-10 px-4">
        <div className="relative mx-auto max-w-2xl text-center">
          <LeafSprig className="pointer-events-none absolute -right-1 top-2 hidden h-14 w-10 -rotate-12 text-[#B7C4AC] sm:block" />
          <LeafSprig className="pointer-events-none absolute -left-1 top-2 hidden h-14 w-10 rotate-12 scale-x-[-1] text-[#B7C4AC] sm:block" />
          <img src={profileImage} alt="" className="mx-auto h-16 w-16 rounded-full border-4 border-[#F6F2E7] bg-white object-cover shadow" />
          <h1 className="mt-3 font-serif text-2xl font-black tracking-tight text-gray-900">{restaurant.name || menuData.title}</h1>
          {settings.welcomeText && <p className="mt-1 text-sm leading-6 text-gray-600">{settings.welcomeText}</p>}
        </div>

        <div className="sticky top-0 z-30 mx-auto mt-5 max-w-5xl bg-[#F6F2E7]/95 pb-3 pt-2 backdrop-blur">
          <label className="relative block">
            <span className="sr-only">جست‌وجوی منو</span>
            <FiSearch className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="جست‌وجوی غذا و نوشیدنی" className="min-h-12 w-full rounded-full border-0 bg-white py-3 pl-4 pr-11 text-sm shadow-sm outline-none focus:ring-2" style={{ "--tw-ring-color": GREEN }} />
          </label>

          <div className="mt-4 flex gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="دسته‌بندی‌های منو">
            <button type="button" aria-pressed={activeCategoryKey === "all"} onClick={() => setActiveCategoryKey("all")} className="flex shrink-0 flex-col items-center gap-1.5">
              <span className="flex h-14 w-14 items-center justify-center rounded-full text-xl shadow-sm transition-colors" style={activeCategoryKey === "all" ? { backgroundColor: GREEN, color: "#fff" } : { backgroundColor: "#fff", color: GREEN }}><FiGrid aria-hidden="true" /></span>
              <span className="text-[11px] font-bold text-gray-700">همه</span>
            </button>
            {categories.map((category) => {
              const key = getCategoryKey(category);
              const Icon = categoryIconsMap[category.icon] || FaUtensils;
              const active = activeCategoryKey === key;
              return (
                <button key={key} type="button" aria-pressed={active} onClick={() => setActiveCategoryKey(key)} className="flex shrink-0 flex-col items-center gap-1.5">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full text-xl shadow-sm transition-colors" style={active ? { backgroundColor: GREEN, color: "#fff" } : { backgroundColor: "#fff", color: GREEN }}><Icon aria-hidden="true" /></span>
                  <span className="max-w-16 truncate text-[11px] font-bold text-gray-700">{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {visibleItems.length ? (
          <div className="mx-auto mt-2 grid max-w-5xl gap-3 pb-6 md:grid-cols-2">
            {visibleItems.map((item, index) => (
              <motion.div
                key={item._id}
                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(index, 8) * 0.04, ease: "easeOut" }}
              >
                <GardenFreshItemCard item={item} quantity={quantities[item._id]} onAdd={addItem} onDecrease={decreaseItem} onClick={openItem} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="mx-auto mt-4 max-w-2xl rounded-3xl border border-dashed border-[#D8CFB4] bg-white/70 px-4 py-14 text-center">
            <FaLeaf className="mx-auto text-3xl" style={{ color: "#B7C4AC" }} aria-hidden="true" />
            <p className="mt-3 font-bold text-gray-800">آیتمی پیدا نشد</p>
            <p className="mt-1 text-sm text-gray-500">عبارت جست‌وجو یا دسته‌بندی را تغییر دهید.</p>
            {searchTerm && <button type="button" onClick={() => { setSearchTerm(""); setActiveCategoryKey("all"); }} className="mt-4 text-sm font-bold" style={{ color: GREEN }}>پاک کردن جست‌وجو</button>}
          </div>
        )}

        <p className="mx-auto mt-6 max-w-md pb-2 text-center text-xs font-bold" style={{ color: GREEN }}>انتخاب سالم برای فردایی بهتر</p>
        {showBamizbanBadge && <BamizbanBadge />}
      </main>

      {cartSummary.itemCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 p-3" dir="rtl">
          <button type="button" onClick={openCart} className="mx-auto flex min-h-12 w-full max-w-xl items-center justify-between rounded-full px-5 font-bold text-white shadow-lg transition-opacity hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-white" style={{ backgroundColor: GREEN }}>
            <span className="flex items-center gap-2"><FiShoppingBag aria-hidden="true" /> مشاهده سبد <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{persianNumber.format(cartSummary.itemCount)} آیتم</span></span>
            <span>{formatPersianPrice(cartSummary.total)}</span>
          </button>
        </div>
      )}

      <MenuIntro open={infoOpen} restaurantData={restaurant} profileImage={profileImage} onClose={() => setInfoOpen(false)} />
    </div>
  );
};

export default GardenFreshMenuTheme;
